import React, { useEffect, useState } from "react";
import Button from "@/components/Base/Button";
import FormInput from "@/components/Base/Form/FormInput";
import FormSelect from "@/components/Base/Form/FormSelect";
import FormTextarea from "@/components/Base/Form/FormTextarea";
import clsx from "clsx";
import Lucide from "@/components/Base/Lucide";
import axios from "axios";
import { getToken } from "@/utils/auth";
import { useNavigate, useParams } from "react-router-dom";
import Alert from "@/components/Base/Alert";

type Mode = "add" | "edit";

interface Props {
    /** Optional override. If not provided, will auto-detect from route param `id` */
    mode?: Mode;
}

interface Project {
    id: string;
    name: string;
}

interface OutletGroup {
    id: string;
    groupName: string;
    companyId: string;
}

interface OutletType {
    id: string;
    typeName: string;
    companyId: string;
}

interface OutletCategory {
    id: string;
    catName: string;
    companyId: string;
}

interface State {
    id: string;
    name: string;
    countryCode: string;
    projectId: string;
}

interface FormData {
    name: string;
    code: string;
    outletGroup: string;
    outletType: string;
    outletCategory: string;
    address: string;
    city: string;
    postcode: string;
    stateId: string;
    country: string;
    contact: string;
    contactPIC: string;
    imageUrl: string;
    projectId: string;
    companyId: string;
    status: string;
    gpsLat: number;
    gpsLong: number
}

interface FormErrors {
    name?: string;
    stateId?: string;
    outletGroup?: string;
    outletType?: string;
    outletCategory?: string;
}

const EMPTY_FORM: FormData = {
    name: "",
    code: "",
    outletGroup: "",
    outletType: "",
    outletCategory: "",
    address: "",
    city: "",
    postcode: "",
    stateId: "",
    country: "",
    contact: "",
    contactPIC: "",
    imageUrl: "",
    projectId: "",
    companyId: "",
    status: "true", // Default to Active
    gpsLat: 0.00,
    gpsLong: 0.00,    
};

export default function EditOutlet({ mode: modeProp }: Props) {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
    const [errors, setErrors] = useState<FormErrors>({});
    const [projects, setProjects] = useState<Project[]>([]);
    const [outletGroups, setOutletGroups] = useState<OutletGroup[]>([]);
    const [outletTypes, setOutletTypes] = useState<OutletType[]>([]);
    const [outletCategories, setOutletCategories] = useState<OutletCategory[]>([]);
    const [states, setStates] = useState<State[]>([]);
    const [submitError, setSubmitError] = useState("");
    const [submitSuccess, setSubmitSuccess] = useState("");

    // Auto-detect mode from URL param
    const mode = modeProp || (id ? "edit" : "add");

    useEffect(() => {
        const init = async () => {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            const token = getToken();

            try {
                setLoading(true);

                // Get companyId from URL params
                const urlParams = new URLSearchParams(window.location.search);
                const companyId = urlParams.get("companyId");


                // Load projects
                try {
                    const projectsUrl = companyId ?
                        `${apiUrl}/projects?companyId=${companyId}` :
                        `${apiUrl}/projects`;
                    const projectsRes = await axios.get(projectsUrl, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setProjects(projectsRes.data?.data || []);
                } catch (err) {
                    console.log("Could not load projects");
                }

                // Load outlet groups
                try {
                    const outletGroupsUrl = companyId ?
                        `${apiUrl}/outlet-groups?companyId=${companyId}` :
                        `${apiUrl}/outlet-groups`;
                    const groupsRes = await axios.get(outletGroupsUrl, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setOutletGroups(groupsRes.data?.data || []);
                } catch (err) {
                    console.log("Could not load outlet groups");
                }

                // Load outlet types
                try {
                    const outletTypesUrl = companyId ?
                        `${apiUrl}/outlet-types?companyId=${companyId}` :
                        `${apiUrl}/outlet-types`;
                    const typesRes = await axios.get(outletTypesUrl, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setOutletTypes(typesRes.data?.data || []);
                } catch (err) {
                    console.log("Could not load outlet types");
                }

                // Load outlet categories
                try {
                    const outletCategoriesUrl = companyId ?
                        `${apiUrl}/outlet-categories?companyId=${companyId}` :
                        `${apiUrl}/outlet-categories`;
                    const categoriesRes = await axios.get(outletCategoriesUrl, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setOutletCategories(categoriesRes.data?.data || []);
                } catch (err) {
                    console.log("Could not load outlet categories");
                }

                // Load states
                try {
                    const statesUrl = companyId ?
                        `${apiUrl}/states?companyId=${companyId}` :
                        `${apiUrl}/states`;
                    const statesRes = await axios.get(statesUrl, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setStates(statesRes.data?.data || []);
                } catch (err) {
                    console.log("Could not load states");
                }

                // If editing, load the outlet
                if (mode === "edit" && id) {
                    const outletRes = await axios.get(`${apiUrl}/outlets/${id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    const outlet = outletRes.data;

                    setFormData({
                        name: outlet.name || "",
                        code: outlet.code || "",
                        outletGroup: outlet.outletGroup || "",
                        outletType: outlet.outletType || "",
                        outletCategory: outlet.outletCategory || "",
                        address: outlet.address || "",
                        city: outlet.city || "",
                        postcode: outlet.postcode || "",
                        stateId: outlet.stateId ||"",
                        country: outlet.country || "",
                        contact: outlet.contact || "",
                        contactPIC: outlet.contactPIC || "",
                        imageUrl: outlet.imageUrl ||"",
                        projectId: outlet.projectId || "",
                        companyId: outlet.companyId ||"",
                        status: outlet.status ? "true" : "false", 
                        gpsLat: outlet.gpsLat?.toString() || "",
                        gpsLong: outlet.gpsLong?.toString() || "",    
                    });
                } else if (companyId) {
                    // Pre-fill companyId for new outlets
                    setFormData(prev => ({ ...prev, companyId }));
                }

            } catch (error) {
                console.error("Failed to initialize:", error);
                setSubmitError("Failed to load data");
            } finally {
                setLoading(false);
            }
        };

        init();
    }, [mode, id]);

    const handleInputChange = (field: keyof FormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error when user starts typing
        if (errors[field as keyof FormErrors]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = "Outlet name is required";
        }

        if (!formData.stateId) {
            newErrors.stateId = "State is required"
        }

        if (!formData.outletGroup) {
            newErrors.outletGroup = "Outlet group is required"
        }

        if (!formData.outletType) {
            newErrors.outletType = "Outlet type is required"
        }

        if (!formData.outletCategory) {
            newErrors.outletCategory = "Outlet category is required"
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setSubmitError("");
        setSubmitSuccess("");

        try {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            const token = getToken();

            // Prepare data for API
            const apiData = {
                name: formData.name,
                code: formData.code || undefined,
                outletGroup: formData.outletGroup || undefined,
                outletType: formData.outletType || undefined,
                outletCategory: formData.outletCategory || undefined,
                address: formData.address || undefined,
                city: formData.city || undefined,
                postcode: formData.postcode || undefined,
                stateId: formData.stateId || undefined,
                country: formData.country || undefined,
                contact: formData.contact || undefined,
                contactPIC: formData.contactPIC || undefined,
                imageUrl: formData.imageUrl || undefined,
                projectId: formData.projectId || undefined,
                companyId: formData.companyId || undefined,
                status: formData.status === "true",
                gpsLat: formData.gpsLat ? Number(formData.gpsLat) : undefined,
                gpsLong: formData.gpsLong ? Number(formData.gpsLong) : undefined    
            };

            if (mode === "add") {
                const queryParams = new URLSearchParams();
                if (formData.companyId) {
                    queryParams.append("companyId", formData.companyId);
                }

                await axios.post(
                    `${apiUrl}/outlets?${queryParams.toString()}`,
                    apiData,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setSubmitSuccess("Outlet created successfully!");
            } else {
                await axios.put(
                    `${apiUrl}/outlets/${id}`,
                    apiData,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setSubmitSuccess("Outlet updated successfully!");
            }

            // Redirect after success
            setTimeout(() => {
                const searchParams = new URLSearchParams(window.location.search);
                const projectId = searchParams.get("projectId");
                const companyId = formData.companyId;
                
                let redirectUrl = "/outlets";
                if (projectId) {
                    redirectUrl += `?projectId=${projectId}`;
                } else if (companyId) {
                    redirectUrl += `?companyId=${companyId}`;
                }
                
                navigate(redirectUrl);
            }, 1500);

        } catch (error: any) {
            console.error("Submit failed:", error);
            const message = error.response?.data?.message || error.message || "An error occurred";
            setSubmitError(message);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        const searchParams = new URLSearchParams(window.location.search);
        const projectId = searchParams.get("projectId");
        const companyId = formData.companyId;
        
        let redirectUrl = "/outlets";
        if (projectId) {
            redirectUrl += `?projectId=${projectId}`;
        } else if (companyId) {
            redirectUrl += `?companyId=${companyId}`;
        }
        
        navigate(redirectUrl);
    };

    if (loading && mode === "edit") {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <Lucide icon="Loader2" className="w-8 h-8 animate-spin mx-auto mb-2" />
                    <div>Loading outlet...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-12 gap-y-10 gap-x-6">
            <div className="col-span-12">
                <div className="flex flex-col mt-4 md:mt-0 md:h-10 gap-y-3 md:items-center md:flex-row">
                    <div className="text-base font-medium group-[.mode--light]:text-white">
                        {mode === "add" ? "Add Outlet" : "Edit Outlet"}
                    </div>
                </div>

                <div className="mt-3.5 grid grid-cols-12 xl:grid-cols-10 gap-y-7 lg:gap-y-10 gap-x-6">
                    <div className="relative flex flex-col col-span-12 lg:col-span-9 xl:col-span-8 gap-y-7">
                        <form onSubmit={handleSubmit}>
                            <div className="flex flex-col p-5 box box--stacked">
                                <div className="p-5 border rounded-[0.6rem] border-slate-200/60 dark:border-darkmode-400">
                                    <div className="flex items-center pb-5 text-[0.94rem] font-medium border-b border-slate-200/60 dark:border-darkmode-400">
                                        <Lucide icon="ChevronDown" className="w-5 h-5 stroke-[1.3] mr-2" />
                                        Outlet Information
                                    </div>

                                    {submitError && (
                                        <Alert variant="outline-danger" className="mt-5">
                                            <Lucide icon="AlertTriangle" className="w-6 h-6" />
                                            {submitError}
                                        </Alert>
                                    )}

                                    {submitSuccess && (
                                        <Alert variant="outline-success" className="mt-5">
                                            <Lucide icon="CheckCircle" className="w-6 h-6" />
                                            {submitSuccess}
                                        </Alert>
                                    )}

                                    <div className="mt-5 grid grid-cols-12 gap-4">
                                        {/* Outlet Name */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">
                                                Outlet Name <span className="text-danger">*</span>
                                            </label>
                                            <FormInput
                                                type="text"
                                                placeholder="Enter outlet name"
                                                value={formData.name}
                                                onChange={(e) => handleInputChange("name", e.target.value)}
                                                className={clsx({ "border-danger": errors.name })}
                                            />
                                            {errors.name && (
                                                <div className="mt-2 text-danger text-xs">{errors.name}</div>
                                            )}
                                        </div>

                                        {/* Outlet Code */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">Code</label>
                                            <FormInput
                                                type="text"
                                                placeholder="Enter outlet code"
                                                value={formData.code}
                                                onChange={(e) => handleInputChange("code", e.target.value)}
                                                className={clsx({ "border-danger": errors.name })}
                                            />
                                            {errors.name && (
                                                <div className="mt-2 text-danger text-xs">{errors.name}</div>
                                            )}
                                        </div>

                                        {/* Address */}
                                        <div className="col-span-12">
                                            <label className="inline-block mb-2">Address</label>
                                            <FormTextarea
                                                placeholder="Enter address"
                                                value={formData.address}
                                                onChange={(e) => handleInputChange("address", e.target.value)}
                                                rows={3}
                                            />
                                        </div>

                                        {/* City */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">City</label>
                                            <FormInput
                                                type="text"
                                                placeholder="Enter city"
                                                value={formData.city}
                                                onChange={(e) => handleInputChange("city", e.target.value)}
                                            />
                                        </div>
                                        
                                        {/* Postcode */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">Postcode</label>
                                            <FormInput
                                                type="text"
                                                placeholder="Enter postcode"
                                                value={formData.postcode}
                                                onChange={(e) => handleInputChange("postcode", e.target.value)}
                                            />
                                        </div>

                                        {/* State */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">
                                                State <span className="text-danger">*</span>
                                            </label>
                                            <FormSelect
                                                value={formData.stateId}
                                                onChange={(e) => handleInputChange("stateId", e.target.value)}
                                                className={clsx({ "border-danger": errors.stateId })}
                                            >
                                                <option value="">Select a state</option>
                                                {states.map((state) => (
                                                    <option key={state.id} value={state.id}>
                                                        {state.name}
                                                    </option>
                                                ))}
                                            </FormSelect>
                                            {errors.stateId && (
                                                <div className="mt-2 text-danger text-xs">{errors.stateId}</div>
                                            )}
                                        </div>

                                        {/* Country */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">Country</label>
                                            <FormInput
                                                type="text"
                                                placeholder="Enter country"
                                                value={formData.country}
                                                onChange={(e) => handleInputChange("country", e.target.value)}
                                            />
                                        </div>

                                        {/* Contact */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">Contact</label>
                                            <FormInput
                                                type="text"
                                                placeholder="Enter contact"
                                                value={formData.contact}
                                                onChange={(e) => handleInputChange("contact", e.target.value)}
                                            />
                                        </div>

                                        {/* Contact PIC */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">Contact PIC</label>
                                            <FormInput
                                                type="text"
                                                placeholder="Enter contact PIC"
                                                value={formData.contactPIC}
                                                onChange={(e) => handleInputChange("contactPIC", e.target.value)}
                                            />
                                        </div>

                                        {/* Outlet Group */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">
                                                Outlet Group <span className="text-danger">*</span>
                                            </label>
                                            <FormSelect
                                                value={formData.outletGroup}
                                                onChange={(e) => handleInputChange("outletGroup", e.target.value)}
                                                className={clsx({ "border-danger": errors.outletGroup })}
                                            >
                                                <option value="">Select an outlet group</option>
                                                {outletGroups.map((group) => (
                                                    <option key={group.id} value={group.groupName}>
                                                        {group.groupName}
                                                    </option>
                                                ))}
                                            </FormSelect>
                                            {errors.outletGroup && (
                                                <div className="mt-2 text-danger text-xs">{errors.outletGroup}</div>
                                            )}
                                        </div>

                                        {/* Type */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">
                                                Outlet Type <span className="text-danger">*</span>
                                            </label>
                                            <FormSelect
                                                value={formData.outletType}
                                                onChange={(e) => handleInputChange("outletType", e.target.value)}
                                                className={clsx({ "border-danger": errors.outletType })}
                                            >
                                                <option value="">Select an outlet type</option>
                                                {outletTypes.map((type) => (
                                                    <option key={type.id} value={type.typeName}>
                                                        {type.typeName}
                                                    </option>
                                                ))}
                                            </FormSelect>
                                            {errors.outletType && (
                                                <div className="mt-2 text-danger text-xs">{errors.outletType}</div>
                                            )}
                                        </div>

                                        {/* Outlet Category and Outlet Image URL in same row */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">
                                                Outlet Category <span className="text-danger">*</span>
                                            </label>
                                            <FormSelect
                                                value={formData.outletCategory}
                                                onChange={(e) => handleInputChange("outletCategory", e.target.value)}
                                                className={clsx({ "border-danger": errors.outletCategory })}
                                            >
                                                <option value="">Select an outlet category</option>
                                                {outletCategories.map((category) => (
                                                    <option key={category.id} value={category.catName}>
                                                        {category.catName}
                                                    </option>
                                                ))}
                                            </FormSelect>
                                            {errors.outletCategory && (
                                                <div className="mt-2 text-danger text-xs">{errors.outletCategory}</div>
                                            )}
                                        </div>

                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">Outlet Image URL</label>
                                            <FormInput
                                                type="url"
                                                placeholder="https://example.com/image.jpg"
                                                value={formData.imageUrl}
                                                onChange={(e) => handleInputChange("imageUrl", e.target.value)}
                                            />
                                        </div>

                                        {/* GPS Coordinates - Lat and Long in same row */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">GPS Latitude</label>
                                            <FormInput
                                                type="number"
                                                step="0.01"
                                                placeholder="Enter GPS Lat coordinates"
                                                value={formData.gpsLat}
                                                onChange={(e) => handleInputChange("gpsLat", e.target.value)}
                                            />
                                        </div>

                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">GPS Longitude</label>
                                            <FormInput
                                                type="number"
                                                step="0.01"
                                                placeholder="Enter GPS Long coordinates"
                                                value={formData.gpsLong}
                                                onChange={(e) => handleInputChange("gpsLong", e.target.value)}
                                            />
                                        </div>


                                        {/* Project - Optional Assignment */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">
                                                Assign to Project (Optional)
                                            </label>
                                            <FormSelect
                                                value={formData.projectId}
                                                onChange={(e) => handleInputChange("projectId", e.target.value)}
                                            >
                                                <option value="">Available to all projects</option>
                                                {projects.map((project) => (
                                                    <option key={project.id} value={project.id}>
                                                        {project.name}
                                                    </option>
                                                ))}
                                            </FormSelect>
                                            <div className="mt-1 text-xs text-slate-500">
                                                Leave empty to make this outlet available to all projects in the company
                                            </div>
                                        </div>

                                        {/* Status */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label className="inline-block mb-2">Status</label>
                                            <FormSelect
                                                value={formData.status}
                                                onChange={(e) => handleInputChange("status", e.target.value)}
                                            >
                                                <option value="true">Active</option>
                                                <option value="false">Inactive</option>
                                            </FormSelect>
                                        </div>

                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex justify-end mt-8 gap-3">
                                        <Button
                                            type="button"
                                            variant="outline-secondary"
                                            onClick={handleCancel}
                                            disabled={loading}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            disabled={loading}
                                        >
                                            {loading && <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />}
                                            {mode === "add" ? "Create Outlet" : "Update Outlet"}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}